import * as React from 'react';
import { useKeyboardHandler } from './useKeyboardHandler';
import { useMouseHandler } from './useMouseHandler';
import { clamp, elementDimension } from '../utils';
import { useEventCallback } from '@fluentui/react-components';
import { EVENTS, UNMEASURED } from '../types';
const clampWithMode = (value, min, max, relative) => {
  return relative || value === UNMEASURED ? value : clamp(value, min, max);
};
const DEFAULT_GET_A11_VALUE_TEXT = value => value === UNMEASURED ? undefined : `${value.toFixed(0)}px`;
export const useResizeHandle = params => {
  const {
    growDirection,
    variableName,
    variableTarget = 'wrapper',
    minValue = 0,
    maxValue = Number.MAX_SAFE_INTEGER,
    onChange,
    onDragStart,
    onDragEnd,
    getA11ValueText = DEFAULT_GET_A11_VALUE_TEXT,
    relative
  } = params;
  const handleRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const elementRef = React.useRef(null);
  const currentValue = React.useRef(UNMEASURED);
  const updateElementsAttrs = React.useCallback(eventData => {
    const a11yValue = relative ?
    // because the currentValue is just the px offset.
    getA11ValueText(elementDimension(elementRef.current, growDirection)) : getA11ValueText(currentValue.current);
    const handleAttributes = {
      role: 'slider',
      'aria-valuemin': minValue,
      ...(maxValue < Number.MAX_SAFE_INTEGER && {
        'aria-valuemax': maxValue
      }),
      ...(a11yValue && {
        'aria-valuetext': a11yValue
      }),
      'aria-orientation': growDirection === 'end' || growDirection === 'start' ? 'horizontal' : 'vertical'
    };
    Object.entries(handleAttributes).forEach(_ref => {
      let [key, value] = _ref;
      var _handleRef_current;
      (_handleRef_current = handleRef.current) == null ? void 0 : _handleRef_current.setAttribute(key, String(value));
    });
    // Make sure to only apply the value if it's not the initial value!
    if (currentValue.current !== UNMEASURED) {
      const targetEl = variableTarget === 'wrapper' ? wrapperRef.current : elementRef.current;
      targetEl == null ? void 0 : targetEl.style.setProperty(variableName, `${currentValue.current}px`);
      onChange == null ? void 0 : onChange(eventData.event, eventData);
    }
  }, [getA11ValueText, maxValue, minValue, onChange, variableName]);
  // In case the maxValue or minValue is changed, we need to make sure we are not exceeding the new limits
  React.useEffect(() => {
    currentValue.current = clampWithMode(currentValue.current, minValue, maxValue, relative);
  }, [maxValue, minValue]);
  const onValueChange = React.useCallback((_, eventData) => {
    const {
      value
    } = eventData;
    const newValue = clampWithMode(value, minValue, maxValue, relative);
    if (newValue !== currentValue.current) {
      // Save the current value, we might need to revert to it if the new value doesn't have any impact on size
      const oldValue = currentValue.current;
      // Measure the size before setting the new value
      const previousSizeInPx = elementDimension(elementRef.current, growDirection);
      // Set the new value and update the elements, this should result in element resize
      currentValue.current = newValue;
      updateElementsAttrs({
        ...eventData,
        value: currentValue.current
      });
      // Measure the size after setting the new value
      const newSizeInPx = elementDimension(elementRef.current, growDirection);
      // If the size hasn't changed, we need to revert to the old value to keep the state and DOM in sync.
      // If we don't do this, then the handle might be stuck in a place where small changes
      // in value don't have any effect.
      if (newSizeInPx === previousSizeInPx) {
        currentValue.current = oldValue;
        updateElementsAttrs({
          ...eventData,
          value: currentValue.current
        });
      }
    }
  }, [minValue, maxValue, updateElementsAttrs]);
  const setValue = React.useCallback(value => {
    const event = new CustomEvent(EVENTS.setValue);
    onValueChange(event, {
      event,
      value,
      type: EVENTS.setValue
    });
  }, [onValueChange]);
  const onDragStartLocal = useEventCallback((e, data) => {
    onDragStart == null ? void 0 : onDragStart(e, {
      ...data,
      value: currentValue.current
    });
  });
  const onDragEndLocal = useEventCallback((e, data) => {
    onDragEnd == null ? void 0 : onDragEnd(e, {
      ...data,
      value: currentValue.current
    });
  });
  const getCurrentValue = React.useCallback(() => {
    return relative ? currentValue.current : elementDimension(elementRef.current, growDirection);
  }, []);
  const {
    attachHandlers: attachMouseHandlers,
    detachHandlers: detachMouseHandlers
  } = useMouseHandler({
    growDirection,
    onValueChange,
    onDragStart: onDragStartLocal,
    onDragEnd: onDragEndLocal,
    getCurrentValue
  });
  const {
    attachHandlers: attachKeyboardHandlers,
    detachHandlers: detachKeyboardHandlers
  } = useKeyboardHandler({
    growDirection,
    onValueChange,
    getCurrentValue
  });
  const setHandleRef = React.useCallback(node => {
    if (node) {
      if (handleRef.current !== node) {
        detachMouseHandlers(node);
        detachKeyboardHandlers(node);
      }
      attachMouseHandlers(node);
      attachKeyboardHandlers(node);
      updateElementsAttrs({
        type: EVENTS.handleRef,
        event: new CustomEvent(EVENTS.handleRef),
        value: currentValue.current
      });
    }
    handleRef.current = node;
  }, [attachKeyboardHandlers, attachMouseHandlers, detachKeyboardHandlers, detachMouseHandlers, updateElementsAttrs]);
  const setWrapperRef = React.useCallback(node => {
    wrapperRef.current = node;
    if (elementRef.current) {
      updateElementsAttrs({
        type: EVENTS.wrapperRef,
        event: new CustomEvent(EVENTS.wrapperRef),
        value: currentValue.current
      });
    }
  }, [updateElementsAttrs]);
  const setElementRef = React.useCallback(node => {
    elementRef.current = node;
    if (elementRef.current) {
      if (currentValue.current === UNMEASURED && relative) {
        currentValue.current = 0;
      }
      updateElementsAttrs({
        type: EVENTS.elementRef,
        event: new CustomEvent(EVENTS.elementRef),
        value: currentValue.current
      });
    }
  }, [updateElementsAttrs]);
  return {
    setValue,
    elementRef: setElementRef,
    handleRef: setHandleRef,
    wrapperRef: setWrapperRef,
    growDirection
  };
};